import { useState } from "react";
import { Plus, Star, Pencil, Trash2, Sun, Moon, Monitor } from "lucide-react";
import clsx from "clsx";
import type { Address } from "@aurelia/types";
import { Button, Input, ConfirmDialog, useConfirmDialog, toast } from "@aurelia/ui";
import { useAddresses, useDeleteAddress } from "../../features/addresses/queries";
import { useChangePassword, useChangeEmail } from "../../features/auth/mutations";
import { useDocumentMeta } from "../../lib/useDocumentMeta";
import { AddressForm } from "../../components/account/AddressForm";
import { useThemeStore, type ThemeMode } from "../../lib/stores/theme";
import { useAuthStore } from "../../lib/stores/auth";

const APPEARANCE_OPTIONS: { value: ThemeMode; label: string; description: string; icon: typeof Sun }[] = [
  { value: "system", label: "System", description: "Matches your device", icon: Monitor },
  { value: "light", label: "Light", description: "Always light", icon: Sun },
  { value: "dark", label: "Dark", description: "Always dark", icon: Moon }
];

function AppearanceSection() {
  const { mode, setMode } = useThemeStore();

  return (
    <div>
      <p className="text-sm font-medium mb-1">Appearance</p>
      <p className="text-sm text-ink-muted mb-4">
        Follow your device's theme automatically, or pick one to always use.
      </p>
      <div className="grid grid-cols-3 gap-3 max-w-sm">
        {APPEARANCE_OPTIONS.map(({ value, label, description, icon: Icon }) => {
          const active = mode === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              aria-pressed={active}
              className={clsx(
                "flex flex-col items-center gap-2 rounded-2xl border p-4 text-center transition-colors duration-300 ease-physical active:scale-95",
                active ? "border-accent bg-accent-soft text-accent-strong" : "border-border bg-surface text-ink-muted hover:text-ink"
              )}
            >
              <Icon size={20} />
              <span className="text-sm font-medium">{label}</span>
              <span className="text-xs text-ink-muted">{description}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AddressCard({ address, onEdit }: { address: Address; onEdit: () => void }) {
  const deleteAddress = useDeleteAddress();
  const deleteConfirm = useConfirmDialog<Address>();

  return (
    <div className="rounded-2xl border border-border bg-surface p-5 flex flex-col gap-2 relative">
      {address.isDefault && (
        <span className="absolute top-4 right-4 flex items-center gap-1 text-xs font-medium text-accent-strong bg-accent-soft px-2 py-1 rounded-full">
          <Star size={12} fill="currentColor" />
          Default
        </span>
      )}
      <p className="text-sm font-medium">{address.label || "Address"}</p>
      <p className="text-sm text-ink">{address.fullName}</p>
      <p className="text-sm text-ink-muted">{address.phone}</p>
      <p className="text-sm text-ink-muted">
        {address.line1}
        {address.line2 ? `, ${address.line2}` : ""}
      </p>
      <p className="text-sm text-ink-muted">
        {address.city}, {address.province} {address.postalCode}
      </p>
      <p className="text-sm text-ink-muted">{address.country}</p>

      <div className="flex gap-4 pt-3 mt-1 border-t border-border">
        <button
          type="button"
          onClick={onEdit}
          className="flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-ink transition-colors duration-300"
        >
          <Pencil size={13} />
          Edit
        </button>
        <button
          type="button"
          onClick={() => deleteConfirm.request(address)}
          className="flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-red-500 transition-colors duration-300"
        >
          <Trash2 size={13} />
          Delete
        </button>
      </div>

      <ConfirmDialog
        open={deleteConfirm.isOpen}
        title="Delete this address?"
        description={deleteConfirm.target ? `"${deleteConfirm.target.label || deleteConfirm.target.line1}" will be removed.` : undefined}
        confirmLabel="Delete"
        danger
        onCancel={deleteConfirm.cancel}
        onConfirm={async () => {
          if (!deleteConfirm.target) return;
          try {
            await deleteAddress.mutateAsync(deleteConfirm.target.id);
            toast("Address deleted", "success");
            deleteConfirm.clear();
          } catch (err) {
            toast((err as Error).message ?? "Failed to delete address", "danger");
          }
        }}
      />
    </div>
  );
}

function AddressesSection() {
  const { data: addresses, isLoading } = useAddresses();
  const [editing, setEditing] = useState<Address | "new" | null>(null);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-medium">Saved addresses</p>
          <p className="text-sm text-ink-muted mt-0.5">
            Save an address here to pick it in one tap at checkout instead of retyping it.
          </p>
        </div>
        {editing === null && (
          <Button type="button" variant="secondary" size="sm" onClick={() => setEditing("new")}>
            <Plus size={14} />
            Add address
          </Button>
        )}
      </div>

      {editing !== null && (
        <div className="mb-6">
          <AddressForm initial={editing === "new" ? undefined : editing} onDone={() => setEditing(null)} />
        </div>
      )}

      {isLoading && <p className="text-sm text-ink-muted">Loading…</p>}

      {!isLoading && (addresses?.length ?? 0) === 0 && editing === null && (
        <p className="text-sm text-ink-muted py-4">No saved addresses yet — add one to speed up checkout.</p>
      )}

      {(addresses?.length ?? 0) > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {addresses!.map((address) => (
            <AddressCard key={address.id} address={address} onEdit={() => setEditing(address)} />
          ))}
        </div>
      )}
    </div>
  );
}

function EmailSection() {
  const user = useAuthStore((s) => s.user);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const changeEmail = useChangeEmail();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    changeEmail.mutate(
      { currentPassword, newEmail },
      {
        onSuccess: () => {
          toast("Email updated", "success");
          setCurrentPassword("");
          setNewEmail("");
        },
        onError: (err) => toast((err as Error).message, "danger")
      }
    );
  };

  return (
    <div>
      <p className="text-sm font-medium mb-1">Change email</p>
      <p className="text-sm text-ink-muted mb-4">
        {user?.email ? (
          <>
            Currently <span className="font-medium text-ink">{user.email}</span>.
          </>
        ) : (
          "No email on file yet."
        )}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-sm">
        <Input
          label="New email"
          type="email"
          autoComplete="email"
          required
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
        />
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          required
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <Button type="submit" disabled={changeEmail.isPending} className="w-fit">
          {changeEmail.isPending ? "Updating…" : "Update email"}
        </Button>
      </form>
    </div>
  );
}

function PasswordSection() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const changePassword = useChangePassword();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          toast("Password updated", "success");
          setCurrentPassword("");
          setNewPassword("");
        },
        onError: (err) => toast((err as Error).message, "danger")
      }
    );
  };

  return (
    <div>
      <p className="text-sm font-medium mb-1">Change password</p>
      <p className="text-sm text-ink-muted mb-4">Used to sign in to your account.</p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-sm">
        <Input
          label="Current password"
          type="password"
          autoComplete="current-password"
          required
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
        <Input
          label="New password"
          type="password"
          autoComplete="new-password"
          minLength={8}
          required
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
        <Button type="submit" disabled={changePassword.isPending} className="w-fit">
          {changePassword.isPending ? "Updating…" : "Update password"}
        </Button>
      </form>
    </div>
  );
}

export function SettingsPage() {
  useDocumentMeta("Account Settings", "Manage your appearance, password and saved addresses.", undefined, undefined, {
    noindex: true
  });

  return (
    <div className="flex flex-col gap-10">
      <AppearanceSection />
      <div className="pt-2 border-t border-border" />
      <EmailSection />
      <div className="pt-2 border-t border-border" />
      <PasswordSection />
      <div className="pt-2 border-t border-border" />
      <AddressesSection />
    </div>
  );
}
